import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Check, ArrowRight, ExternalLink, Loader2, CreditCard,
  Users, Sparkles, ClipboardList, Compass, HeartHandshake, Phone, Star, Plus, Minus, Settings2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { PAGE, TableSkeleton, CopyButton, money, moneyPrice, plural, fmtDate } from '../../components/ui';
import { Notice } from '../Financials/financeKit';

/**
 * Subscription and checkout.
 *
 * The page only ever sends a plan CODE to the server — never a price. Amounts shown here
 * come from the same server catalogue that checkout charges from, so what a customer is
 * quoted and what they are billed cannot drift apart, and nothing in this file can be
 * edited in devtools to change what something costs.
 */

const ADDON_ICONS = {
  plan_management: ClipboardList,
  support_coordination: Compass,
  aged_care: HeartHandshake,
  ai_receptionist: Phone,
  reputation: Star,
  extra_participants: Users,
};

// Two formatters, on purpose. A catalogue price may drop '.00' ($199). Anything the org is or was
// charged — an invoice, the checkout total — is always written to the cent, so it matches the
// Stripe receipt exactly.
const price = (cents) => (cents == null ? null : moneyPrice(cents / 100));
const charged = (cents) => (cents == null ? null : money(cents / 100));

const SUB_STATUS = {
  active: { label: 'Active', cls: 'badge-green' },
  trialing: { label: 'Trial', cls: 'badge-green' },
  past_due: { label: 'Payment overdue', cls: 'badge-yellow' },
  incomplete: { label: 'Activating', cls: 'badge-yellow' },
  canceled: { label: 'Cancelled', cls: 'badge-gray' },
  cancelled: { label: 'Cancelled', cls: 'badge-gray' },
};
const subStatus = (s) => SUB_STATUS[s] || { label: String(s || '').replace(/_/g, ' '), cls: 'badge-gray' };

const CONTACT_EMAIL = 'info@rostery.com.au';

export default function Billing() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [catalogue, setCatalogue] = useState(null);
  const [current, setCurrent] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [interval, setInterval_] = useState('month');
  const [planCode, setPlanCode] = useState(null);
  const [userPicked, setUserPicked] = useState(false);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [extraSeats, setExtraSeats] = useState(0);
  const [extraBranches, setExtraBranches] = useState(0);

  // ── Load ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [cat, sub] = await Promise.all([
          api.get('/billing/catalogue'),
          api.get('/billing/subscription'),
        ]);
        if (!alive) return;
        setCatalogue(cat.data);
        setCurrent(sub.data);
        // Pre-select what they already have, so the page opens on their situation
        // rather than on a blank slate.
        if (sub.data?.subscription) {
          setPlanCode(sub.data.subscription.plan_code);
          setInterval_(sub.data.subscription.interval || 'month');
          setSelectedAddons((sub.data.addons || [])
            .filter(a => !['extra_participants', 'extra_branches'].includes(a.code))
            .map(a => a.code));
          const extra = (sub.data.addons || []).find(a => a.code === 'extra_participants');
          setExtraSeats(extra ? extra.quantity : 0);
          const br = (sub.data.addons || []).find(a => a.code === 'extra_branches');
          setExtraBranches(br ? br.quantity : 0);
        } else {
          // A plan chosen on the public pricing page arrives as ?plan=…&interval=…, so the
          // choice survives the trip through login instead of resetting to a default.
          const wanted = params.get('plan');
          const known = (cat.data.plans || []).some(p => p.code === wanted);
          setPlanCode(known ? wanted : 'growth');
          if (known) setUserPicked(true);
          if (params.get('interval') === 'year') setInterval_('year');
        }
        api.get('/billing/invoices').then(r => alive && setInvoices(r.data || [])).catch(() => {});
      } catch (e) {
        if (alive) toast.error(e.response?.data?.message || 'Could not load billing');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // Deliberately runs once: the URL is read as an opening choice, not a live binding.
  }, []);

  // ── Return from Stripe ──────────────────────────────────────────────────────
  useEffect(() => {
    const r = params.get('checkout');
    if (!r) return;
    if (r === 'success') {
      // Stripe redirects back before the webhook necessarily lands, so the subscription
      // may still say "incomplete" for a moment. Say so rather than showing a stale plan.
      toast.success('Payment received. Activating your subscription…');
      const t = setTimeout(() => {
        api.get('/billing/subscription').then(res => setCurrent(res.data)).catch(() => {});
      }, 2500);
      params.delete('checkout'); params.delete('session'); setParams(params, { replace: true });
      return () => clearTimeout(t);
    }
    if (r === 'cancelled') {
      toast('Checkout cancelled. Nothing was charged.');
      params.delete('checkout'); setParams(params, { replace: true });
    }
  }, [params, setParams]);

  // Defaults to true so a catalogue that predates this field does not hide a working
  // checkout button.
  const checkoutEnabled = catalogue?.checkout_enabled !== false;
  const plans = catalogue?.plans || [];
  const addons = catalogue?.addons || [];
  const seatAddon = addons.find(a => a.code === 'extra_participants');
  const branchAddon = addons.find(a => a.code === 'extra_branches');
  const moduleAddons = addons.filter(a => !a.per_unit);
  const chosenPlan = plans.find(p => p.code === planCode) || null;

  const total = useMemo(() => {
    if (!chosenPlan || chosenPlan.contact_only) return null;
    const key = interval === 'year' ? 'annual_cents' : 'monthly_cents';
    let sum = chosenPlan[key] || 0;
    for (const code of selectedAddons) {
      const a = addons.find(x => x.code === code);
      if (a) sum += a[key] || 0;
    }
    if (extraSeats > 0 && seatAddon) sum += (seatAddon[key] || 0) * extraSeats;
    if (extraBranches > 0 && branchAddon) sum += (branchAddon[key] || 0) * extraBranches;
    return sum;
  }, [chosenPlan, selectedAddons, extraSeats, extraBranches, interval, addons, seatAddon, branchAddon]);

  const toggleAddon = (code) =>
    setSelectedAddons(s => (s.includes(code) ? s.filter(c => c !== code) : [...s, code]));

  async function startCheckout() {
    if (!chosenPlan) return;
    if (chosenPlan.contact_only) { window.location.href = '/contact'; return; }
    setBusy(true);
    try {
      // Only codes cross the wire. The server resolves every price.
      const { data } = await api.post('/billing/checkout', {
        plan: planCode,
        interval,
        addons: selectedAddons,
        extra_participants: extraSeats,
        extra_branches: extraBranches,
      });
      if (data?.url) { window.location.href = data.url; return; }
      // An existing subscriber is changed in place — there is no Stripe page to send them
      // to, so refresh what they now have rather than leaving stale numbers on screen.
      if (data?.updated) {
        toast.success(data.message || 'Subscription updated');
        const [sub, inv] = await Promise.all([
          api.get('/billing/subscription'),
          api.get('/billing/invoices').catch(() => ({ data: [] })),
        ]);
        setCurrent(sub.data);
        setInvoices(inv.data || []);
        return;
      }
      toast.error('Could not start checkout');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not start checkout');
    } finally {
      setBusy(false);
    }
  }

  async function openPortal() {
    setBusy(true);
    try {
      const { data } = await api.post('/billing/portal');
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not open the billing portal');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className={PAGE}>
        <div className="card"><TableSkeleton rows={3} cols={3} /></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map(i => <div key={i} className="skeleton h-44 rounded-xl" />)}
        </div>
      </div>
    );
  }

  const sub = current?.subscription;
  const usage = current?.usage;
  const overLimit = usage?.participant_limit != null && usage.participants > usage.participant_limit;
  const per = interval === 'year' ? 'yr' : 'mo';
  const key = interval === 'year' ? 'annual_cents' : 'monthly_cents';
  const currentPlan = sub ? plans.find(p => p.code === sub.plan_code) : null;
  const switching = !!(sub && chosenPlan && chosenPlan.code !== sub.plan_code);
  const planDelta = switching && currentPlan && chosenPlan[key] != null && currentPlan[key] != null
    ? chosenPlan[key] - currentPlan[key] : null;

  return (
    <div className={PAGE}>
      <p className="text-sm text-slate-600 -mb-2">
        Choose a plan and the modules you need. Cancel or change at any time.
      </p>

      {/* ── Current subscription ───────────────────────────────────────────── */}
      {sub && (
        <div className="card p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg font-semibold text-slate-900">{sub.plan_name}</span>
                <span className={clsx(subStatus(sub.status).cls, 'whitespace-nowrap')}>{subStatus(sub.status).label}</span>
              </div>
              {sub.current_period_end && (
                <p className="text-sm text-slate-600">
                  {sub.cancel_at_period_end ? 'Ends' : 'Renews'} {fmtDate(sub.current_period_end)}
                </p>
              )}
              {current?.addons?.length > 0 && (
                <p className="text-sm text-slate-600 mt-1">
                  Add-ons: {current.addons.map(a => a.name).join(', ')}
                </p>
              )}
            </div>
            <button type="button" onClick={openPortal} disabled={busy} className="btn-secondary">
              Manage payment and invoices <ExternalLink size={14} />
            </button>
          </div>

          {usage && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-100">
              <Usage label="Participants" used={usage.participants} limit={usage.participant_limit} />
              <Usage label="Staff accounts" used={usage.staff} limit={usage.staff_limit} />
              <Usage label="Branches" used={usage.branches ?? 0} limit={usage.branch_limit} />
            </div>
          )}

          {sub.status === 'past_due' && (
            <Notice tone="amber" className="mt-4">
              Your last payment did not go through. Your account still works; update your card in the billing portal to avoid interruption.
            </Notice>
          )}
          {overLimit && (
            <Notice tone="amber" className="mt-3">
              You have {usage.participants} active participants on a plan that includes {usage.participant_limit}. Add participant capacity below, or move up a plan.
            </Notice>
          )}
        </div>
      )}

      {!checkoutEnabled && (
        <Notice tone="amber" title="Online payment isn't switched on yet">
          You can see the plans and work out what you need, but card payment isn&apos;t available on this account yet.{' '}
          {user?.role === 'super_admin'
            ? <>Add your Stripe keys in <b>Super Admin › Billing</b> to turn it on.</>
            : <>Call us on <a href="tel:+61468447062" className="font-semibold underline">+61 468 447 062</a> or email{' '}
               <span className="font-semibold">{CONTACT_EMAIL}</span><CopyButton value={CONTACT_EMAIL} title="Copy email address" className="ml-1 align-middle" />{' '}
               and we&apos;ll set your subscription up.</>}
        </Notice>
      )}

      {/* ── Billing interval ───────────────────────────────────────────────── */}
      <div className="flex items-center justify-center">
        <div role="group" aria-label="Billing interval" className="inline-flex rounded-lg p-0.5 bg-slate-100">
          {['month', 'year'].map(i => (
            <button key={i} type="button" aria-pressed={interval === i} onClick={() => setInterval_(i)}
              className={clsx('min-h-[36px] px-4 rounded-md text-sm font-semibold transition-colors duration-150',
                interval === i ? 'bg-white shadow-[0_1px_2px_rgba(16,24,40,.08)] text-slate-900' : 'text-slate-600 hover:text-slate-900')}>
              {i === 'month' ? 'Monthly' : 'Annual'}
              {i === 'year' && <span className="ml-1.5 text-[11px] font-semibold text-emerald-700">2 months free</span>}
            </button>
          ))}
        </div>
      </div>

      {/* ── Plans ─────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map(p => {
          const pc = p[key];
          const chosen = planCode === p.code;
          const isCurrent = sub && sub.plan_code === p.code;
          // The ring marks a choice that differs from what they have; their own plan is marked
          // "Current plan" instead, so nobody wonders whether they already changed it.
          const ring = chosen && (!sub || !isCurrent);
          return (
            <button key={p.code} type="button" aria-pressed={chosen}
              onClick={() => { setPlanCode(p.code); setUserPicked(true); }}
              className={clsx('flex flex-col items-start justify-start h-full text-left rounded-xl border-2 p-5 transition-colors duration-150 bg-white',
                ring ? 'border-primary-600 shadow-card-deep' : 'border-slate-200 hover:border-slate-300')}>
              <div className="flex w-full items-center justify-between gap-2 mb-1">
                <span className="font-semibold text-slate-900">{p.name}</span>
                {isCurrent
                  ? <span className="badge-gray whitespace-nowrap">Current plan</span>
                  : p.badge && <span className="badge-gray whitespace-nowrap !text-[10px]">{p.badge}</span>}
              </div>
              <p className="text-xs text-slate-600 leading-snug mb-3 min-h-[32px]">{p.subtitle}</p>
              <p className="text-2xl font-bold text-slate-900 tabular-nums">
                {pc ? price(pc) : 'Custom'}
                {pc ? <span className="text-sm font-medium text-slate-500">/{per}</span> : null}
              </p>
              <p className="text-xs text-slate-600 mt-2">
                {p.participant_limit ? `Up to ${p.participant_limit} participants` : 'Unlimited participants'}
                {' · '}
                {p.staff_limit ? `${p.staff_limit} staff` : 'unlimited staff'}
              </p>
              {ring && (
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary-700">
                  <Check size={13} /> {sub ? `Switch to ${p.name}` : userPicked ? 'Your choice' : 'Recommended'}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Add-ons ───────────────────────────────────────────────────────── */}
      <div>
        <h2 className="text-base font-semibold text-slate-900 mb-1">Add modules</h2>
        <p className="text-sm text-slate-600 mb-4">
          Separate services with their own workflows. Add or remove them whenever you like.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {moduleAddons.map(a => {
            const Icon = ADDON_ICONS[a.code] || Sparkles;
            const on = selectedAddons.includes(a.code);
            const ac = a[key];
            return (
              <button key={a.code} type="button" aria-pressed={on} onClick={() => toggleAddon(a.code)}
                className={clsx('flex flex-col items-start justify-start h-full text-left rounded-xl border-2 p-4 transition-colors duration-150 bg-white',
                  on ? 'border-primary-600' : 'border-slate-200 hover:border-slate-300')}>
                <div className="flex w-full items-start gap-3">
                  <span className={clsx('w-9 h-9 rounded-lg grid place-items-center shrink-0', on ? 'bg-primary-600' : 'bg-slate-100')}>
                    <Icon size={17} className={on ? 'text-white' : 'text-slate-500'} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-sm text-slate-900">{a.name}</p>
                      <span className="text-sm font-bold text-slate-900 shrink-0 tabular-nums">
                        {price(ac)}<span className="text-[11px] font-medium text-slate-500">/{per}</span>
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 leading-snug mt-0.5">{a.subtitle}</p>
                    {a.metered && (
                      <p className="text-[11px] text-slate-500 mt-1">
                        {a.metered.included} {a.metered.unit}s included, then {moneyPrice(a.metered.overage_cents / 100)}/{a.metered.unit}
                      </p>
                    )}
                    {on && <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-primary-700"><Check size={12} /> Added</p>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Extra participants — a quantity, not a toggle */}
      {seatAddon && chosenPlan?.participant_limit != null && (
        <div className="card p-4 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-sm text-slate-900">Additional participants</p>
            <p className="text-xs text-slate-600 mt-0.5">
              {chosenPlan.name} includes {chosenPlan.participant_limit}. Add more at {price(seatAddon[key])} each. The buttons add or remove 5 at a time.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setExtraSeats(n => Math.max(0, n - 5))} aria-label="Remove 5 participants"
              className="w-10 h-10 rounded-lg border border-slate-300 grid place-items-center text-slate-700 hover:bg-slate-50"><Minus size={15} /></button>
            <input type="number" min={0} max={5000} value={extraSeats} aria-label="Additional participants"
              onChange={e => setExtraSeats(Math.max(0, Math.min(5000, parseInt(e.target.value, 10) || 0)))}
              className="input-field !w-20 text-center font-semibold tabular-nums" />
            <button type="button" onClick={() => setExtraSeats(n => Math.min(5000, n + 5))} aria-label="Add 5 participants"
              className="w-10 h-10 rounded-lg border border-slate-300 grid place-items-center text-slate-700 hover:bg-slate-50">
              <Plus size={15} />
            </button>
          </div>
        </div>
      )}

      {/* Extra branches — same shape as participants, because a provider opening a
          fourth site should be able to buy one rather than jump a whole tier for it. */}
      {branchAddon && chosenPlan?.branch_limit != null && (
        <div className="card p-4 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-sm text-slate-900">Additional branches</p>
            <p className="text-xs text-slate-600 mt-0.5">
              {chosenPlan.name} includes {chosenPlan.branch_limit}{' '}
              {chosenPlan.branch_limit === 1 ? 'branch' : 'branches'}. Add more at {price(branchAddon[key])} each.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setExtraBranches(n => Math.max(0, n - 1))} aria-label="Remove a branch"
              className="w-10 h-10 rounded-lg border border-slate-300 grid place-items-center text-slate-700 hover:bg-slate-50"><Minus size={15} /></button>
            <input type="number" min={0} max={200} value={extraBranches} aria-label="Additional branches"
              onChange={e => setExtraBranches(Math.max(0, Math.min(200, parseInt(e.target.value, 10) || 0)))}
              className="input-field !w-20 text-center font-semibold tabular-nums" />
            <button type="button" onClick={() => setExtraBranches(n => Math.min(200, n + 1))} aria-label="Add a branch"
              className="w-10 h-10 rounded-lg border border-slate-300 grid place-items-center text-slate-700 hover:bg-slate-50">
              <Plus size={15} />
            </button>
          </div>
        </div>
      )}

      {/* ── Summary + checkout ────────────────────────────────────────────── */}
      <div className="card border-primary-200 p-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-sm text-slate-700">
            {switching
              ? <>Change: {currentPlan?.name || sub.plan_name} <ArrowRight size={12} className="inline -mt-0.5" aria-hidden="true" /> {chosenPlan.name}
                  {planDelta != null && planDelta !== 0 && <> · {planDelta > 0 ? '+' : '-'}{price(Math.abs(planDelta))}/{per}</>}</>
              : sub ? <>Current plan: {chosenPlan?.name}</> : chosenPlan?.name}
            {selectedAddons.length > 0 && ` + ${plural(selectedAddons.length, 'add-on')}`}
            {extraSeats > 0 && ` + ${extraSeats} participants`}
            {extraBranches > 0 && ` + ${plural(extraBranches, 'branch', 'branches')}`}
          </p>
          <p className="text-3xl font-bold text-slate-900 mt-1 tabular-nums">
            {total == null ? 'Custom pricing' : charged(total)}
            {total != null && (
              <span className="text-base font-medium text-slate-500">
                /{interval === 'year' ? 'year' : 'month'}
              </span>
            )}
          </p>
          <p className="text-xs text-slate-600 mt-1">
            Prices in AUD, excluding GST. Charged securely by Stripe — we never see your card.
          </p>
        </div>
        {checkoutEnabled ? (
          <button type="button" onClick={startCheckout} disabled={busy || !chosenPlan} className="btn-primary btn-lg">
            {busy ? <Loader2 size={17} className="animate-spin" /> : <CreditCard size={17} />}
            {chosenPlan?.contact_only ? 'Talk to sales'
              : sub ? 'Update subscription' : 'Continue to payment'}
            {!busy && <ArrowRight size={16} />}
          </button>
        ) : user?.role === 'super_admin' ? (
          <a href="/superadmin" className="btn-primary btn-lg">
            <Settings2 size={17} /> Set up Stripe <ArrowRight size={16} />
          </a>
        ) : (
          <div className="text-sm text-slate-700">
            <p className="font-semibold text-slate-900">Talk to us to subscribe</p>
            <p className="mt-0.5 flex items-center gap-1">
              Email <span className="font-semibold">{CONTACT_EMAIL}</span>
              <CopyButton value={CONTACT_EMAIL} title="Copy email address" label="Copy" />
            </p>
            <p className="mt-0.5">or call <a href="tel:+61468447062" className="font-semibold underline">+61 468 447 062</a></p>
          </div>
        )}
      </div>

      {/* ── Invoices ──────────────────────────────────────────────────────── */}
      {invoices.length > 0 && (
        <div>
          <h2 className="text-base font-semibold text-slate-900 mb-3">Invoices</h2>
          <div className="card overflow-hidden divide-y divide-slate-100">
            {invoices.map((inv, i) => (
              <div key={inv.number || i} className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 text-sm">
                <div>
                  <span className="font-semibold text-slate-800">{inv.number || '—'}</span>
                  <span className="text-slate-600 ml-3">{inv.created_at ? fmtDate(inv.created_at) : ''}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold tabular-nums text-slate-900">
                    {charged(inv.amount_paid_cents ?? inv.amount_due_cents)}
                  </span>
                  <span className={clsx(inv.status === 'paid' ? 'badge-green' : 'badge-gray', 'whitespace-nowrap')}>
                    {inv.status === 'paid' ? 'Paid' : String(inv.status || '').replace(/_/g, ' ')}
                  </span>
                  {inv.pdf_url && (
                    <a href={inv.pdf_url} target="_blank" rel="noreferrer" aria-label={`Invoice ${inv.number || ''} PDF (opens in a new tab)`}
                      className="text-primary-700 hover:underline text-xs font-semibold">PDF</a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}

function Usage({ label, used, limit }) {
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const over = limit != null && used > limit;
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-slate-600">{label}</span>
        <span className={clsx('font-semibold tabular-nums', over ? 'text-amber-700' : 'text-slate-800')}>
          {used}{limit != null ? ` / ${limit}` : ''}
          {limit == null && <span className="text-slate-500 font-normal"> · unlimited</span>}
        </span>
      </div>
      {limit != null && (
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden" role="progressbar" aria-label={label}
          aria-valuemin={0} aria-valuemax={limit} aria-valuenow={Math.min(used, limit)}>
          <div className={clsx('h-full rounded-full', over ? 'bg-amber-500' : 'bg-primary-600')} style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}
